<template>
    <div ref="wrapper" class="datepicker-wrapper dropdown-wrapper-js" @click.stop>
        <button @click.stop="toggleDatePicker"
            v-b-tooltip.hover.top="tooltip"
            class="btn btn-light fs-12 py-1 px-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
            :class="selectedDate ? 'py-0' : 'py-1'">
            <img :src="icon" class="flex-shrink-0" width="14" alt="">
            <p v-if="selectedDate" class="mb-0 fw-medium">{{ formatDate(selectedDate) }}</p>
            <img src="/img/x-black.svg" width="16" @click.stop="clearDate" v-if="selectedDate"
                class="border-0 bg-transparent px-1 mx-1">
        </button>
        <date-picker v-if="isOpen" class="position-absolute dropdown-menu p-0 dropdown-js" :value="selectedDate"
            @input="handleDateSelect" inline lang="en" format="MM-dd-yyyy" />
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import { formatDate } from '../../utils/helpers';
import { MIN_DATE, JOB_DATE_TYPE } from '../../utils/constants';

const LOCALE = 'sv-SE';
const TIME_SEPARATOR = ' ';
const ISO_SEPARATOR = 'T';
const ISO_MILLISECONDS = '.000Z';

export default {
    name: 'DatePickerButton',
    components: {
        DatePicker
    },
    props: {
        value: {
            type: [Date, String],
            default: null
        },
        placeholder: {
            type: String,
            default: 'MM-DD-YYYY'
        },
        icon: {
            type: String,
            default: '/img/calendar-time.svg'
        },
        tooltip: {
            type: String,
            default: 'Select Date'
        },
        isOpen: {
            type: Boolean,
            default: false
        },
        dateType: {
            type: String,
            default: JOB_DATE_TYPE.START,
            validator: value => [JOB_DATE_TYPE.START, JOB_DATE_TYPE.END].includes(value)
        },
        otherDateValue: {
            type: [Date, String],
            default: null
        },
        otherDateType: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            selectedDate: this.value
        };
    },
    watch: {
        value(newVal) {
            this.selectedDate = newVal;
        }
    },
    methods: {
        formatDate,
        toggleDatePicker() {
            if (this.isOpen) {
                this.$emit('close');
            } else {
                this.$emit('open');
            }
        },
        handleDateSelect(date) {
            const localizedDate = new Date(date).toLocaleString(LOCALE, { hour12: false });
            const isoLikeString = localizedDate.replace(TIME_SEPARATOR, ISO_SEPARATOR) + ISO_MILLISECONDS;

            const finalIsoDate = new Date(isoLikeString).toISOString();

            // Validate date constraints
            if (this.otherDateValue && this.otherDateType) {
                const isValid = this.validateDateConstraint(finalIsoDate, this.otherDateValue, this.dateType, this.otherDateType);
                if (!isValid) {
                    return;
                }
            }

            this.selectedDate = finalIsoDate;
            this.$emit('input', finalIsoDate);
            this.$emit('close');
        },
        validateDateConstraint(newDate, otherDate, currentType, otherType) {
            if (!otherDate || !currentType || !otherType) return true;

            const newDateObj = new Date(newDate);
            const otherDateObj = new Date(otherDate);

            // Start date must be before end date
            if (currentType === JOB_DATE_TYPE.START && otherType === JOB_DATE_TYPE.END) {
                if (newDateObj > otherDateObj) {
                    this.$toast('Start date must be before end date', {
                        title: 'Invalid dates',
                        variant: 'danger',
                    });
                    return false;
                }
            }

            // End date must be after start date
            if (currentType === JOB_DATE_TYPE.END && otherType === JOB_DATE_TYPE.START) {
                if (newDateObj < otherDateObj) {
                    this.$toast('End date must be after start date', {
                        title: 'Invalid dates',
                        variant: 'danger',
                    });
                    return false;
                }
            }

            return true;
        },
        clearDate() {
            this.selectedDate = null;
            this.$emit('input', MIN_DATE);
            this.$emit('close');
        },
    },
    mounted() {
    },
};
</script>

<style scoped>
.btn {
    line-height: 12px;
}

.datepicker-wrapper {
    position: relative;
    display: inline-block;
}

.datepicker-wrapper ::v-deep .mx-calendar {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    z-index: 1050 !important;
}

.datepicker-wrapper ::v-deep .mx-datepicker-main {
    z-index: 1050 !important;
}

/* my ones */
::v-deep .mx-btn-icon-double-left,
::v-deep .mx-btn-icon-double-right {
    display: none !important;
}

::v-deep .mx-btn-current-year {
    font-size: 14px;
    font-weight: 600;
    color: #1B1B1C;
}

::v-deep .mx-btn-current-month {
    font-size: 14px;
    font-weight: 600;
    color: #1B1B1C;
}

::v-deep .mx-table-date thead th {
    font-size: 14px;
    font-weight: 600;
    color: #1B1B1C;
}

::v-deep .mx-date-row {
    font-size: 14px;
    font-weight: 400;
    color: #1B1B1C;
}

::v-deep.mx-datepicker-main {
    border-radius: 8px !important;
    border: none !important;
}

::v-deep .mx-calendar-header {
    border-bottom: 1px solid #0000000F;
    padding-left: 12px;
    padding-right: 12px;
}

::v-deep .mx-calendar-content {
    padding-left: 12px;
    padding-right: 12px;
}

::v-deep .mx-btn-icon-left .mx-icon-left::before,
::v-deep .mx-btn-icon-right .mx-icon-right::before {
    color: #111827 !important;
    border-width: 3px 0 0 3px;
}

::v-deep .mx-datepicker-body {
    padding: 0px;
}

::v-deep .mx-calendar-panel-date {
    padding: 6px 0px;
}
</style>
